import React, { useCallback } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import BuildIcon from '@mui/icons-material/Build'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import { AccountType, ResultObjectType, SystemAccountType } from '@cbgms/api'
import FullName from '@cbgms/components/src/utils/FullName'
import { ResultTitle } from './Title'
import { manufacturerToClass } from '@cbgms/components/src/utils/vehicle'
import Icon from '@mui/material/Icon'
import LicensePlate from '@cbgms/components/form/LicensePlate'
import { VinText } from '@carsys/ui'
import { AccountIcon } from '@cbgms/components/src/utils/AccountIcon'
import { useSearchResultTypeContext } from '../../context/SearchResultTypeContext'

export interface WorkorderResultType {
  Type: ResultObjectType
  WorkOrder: WorkOrderProps
  clearQuery: () => void
  primaryAction: () => Promise<void>
  hasGroupHeader: boolean
}

interface WorkOrderProps {
  CustomerSystemType: SystemAccountType
  CustomerType: AccountType
  CustomerFirstName: string
  CustomerName: string
  CustomerPrefix: string
  CreatedAt: string
  ID: string
  UUID: string
  VehicleManufacturer: string
  VehicleModel: string
  VehicleRegistration: string
  VehicleType: string
  VehicleVIN: string
}

export const WorkOrderResult: React.FC<WorkorderResultType> = props => {
  const { WorkOrder, Type, hasGroupHeader, primaryAction } = props

  const { isPortal } = useSearchResultTypeContext()
  const onClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation()
      primaryAction()
    },
    [primaryAction]
  )

  return (
    <Box sx={{ width: '100%', cursor: 'pointer' }} onClick={onClick}>
      <Grid container display='flex' flexWrap='nowrap' justifyContent='space-between'>
        <Grid item xs={7}>
          <ResultTitle resultType={Type} isTitleVisible={!hasGroupHeader} />
          <Box display='flex' alignItems='flex-start'>
            {isPortal ? (
              <ShoppingCartOutlinedIcon fontSize='small' sx={theme => ({ mr: 1, color: theme.palette.LKQGrey.background.modest })} />
            ) : (
              <BuildIcon fontSize='small' sx={theme => ({ mr: 1, color: theme.palette.LKQGrey.background.modest })} />
            )}
            <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
              <Typography sx={{ fontWeight: 500 }}>{WorkOrder.ID}</Typography>
              <Box display='block'>
                {WorkOrder.VehicleManufacturer && (
                  <Typography noWrap sx={{ marginRight: 0.5, textOverflow: 'ellipsis' }}>{`${WorkOrder.VehicleManufacturer} ${
                    WorkOrder?.VehicleModel || ''
                  } ${WorkOrder.VehicleType || ''}`}</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={5}>
          <Box display='flex' justifyContent='flex-end'>
            {WorkOrder.VehicleManufacturer && (
              <Icon
                className={`icon vehicle brand ${manufacturerToClass(WorkOrder.VehicleManufacturer)}`}
                fontSize='medium'
                sx={{ marginRight: 0.5 }}
              />
            )}
            {WorkOrder.VehicleRegistration ? (
              <LicensePlate size='small' registration={WorkOrder.VehicleRegistration} />
            ) : WorkOrder.VehicleVIN ? (
              <VinText value={WorkOrder.VehicleVIN} />
            ) : null}
          </Box>
          <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {WorkOrder?.CustomerName && WorkOrder?.CustomerSystemType !== SystemAccountType.WalkIn && (
              <Typography noWrap component='p' sx={{ display: 'flex', textOverflow: 'ellipsis', alignItems: 'center' }}>
                <AccountIcon accountType={WorkOrder.CustomerType || AccountType.Private} />
                <FullName
                  firstName={WorkOrder.CustomerFirstName || ''}
                  lastName={WorkOrder.CustomerName || ''}
                  prefix={WorkOrder.CustomerPrefix}
                />
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
